import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, ExternalLink, Clock, Mail, Loader2 } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface ValidationCheck {
  robotsTxtAccessible: boolean;
  sitemapUrlPresent: boolean;
  sitemapUrlCorrect: boolean;
  sitemapAccessible: boolean;
  sitemapValidXml: boolean;
  sitemapUrlCount: number;
  disallowRulesCorrect: boolean;
}

interface ValidationReport {
  valid: boolean;
  timestamp: string;
  checks: ValidationCheck;
  issues: string[];
  recommendations: string[];
}

interface ValidationLog {
  id: string;
  timestamp: string;
  valid: boolean;
  checks: ValidationCheck;
  issues: string[];
  recommendations: string[];
  email_sent: boolean;
  email_sent_at: string | null;
}

const SeoValidierung = () => {
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<ValidationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const { data, error } = await supabase
        .from("seo_validation_logs")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(30);

      if (error) throw error;

      setLogs((data as unknown as ValidationLog[]) || []);
    } catch (error) {
      console.error("Fehler beim Laden der Historie:", error);
    } finally {
      setLogsLoading(false);
    }
  };

  const runValidation = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('validate-robots');
      
      if (error) {
        console.error('Validation error:', error);
        toast.error('Validierung fehlgeschlagen: ' + error.message);
        return;
      }

      setReport(data);
      
      if (data.valid) {
        toast.success('✅ Alle Checks bestanden!');
      } else {
        toast.warning(`⚠️ ${data.issues.length} Problem(e) gefunden`);
      }
      
      // Reload logs to show the new entry
      loadLogs();
    } catch (error) {
      console.error('Error running validation:', error);
      toast.error('Fehler beim Ausführen der Validierung');
    } finally {
      setLoading(false);
    }
  };

  const getCheckIcon = (passed: boolean) => {
    return passed ? (
      <CheckCircle2 className="w-5 h-5 text-green-500" />
    ) : (
      <XCircle className="w-5 h-5 text-red-500" />
    );
  };

  const getStatusBadge = (passed: boolean) => {
    return (
      <Badge variant={passed ? "default" : "destructive"}>
        {passed ? "✅ OK" : "❌ Fehler"}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>SEO Validierung - Admin</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
              SEO Validierung
            </h1>
            <p className="text-muted-foreground mt-2">
              Automatische Überprüfung der robots.txt und Sitemap-Konfiguration
            </p>
          </div>
          
          <Button 
            onClick={runValidation} 
            disabled={loading}
            size="lg"
          >
            {loading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Wird validiert...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Jetzt validieren
              </>
            )}
          </Button>
        </div>

        {report && (
          <>
            {/* Gesamtstatus */}
            <Alert variant={report.valid ? "default" : "destructive"}>
              {report.valid ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertTitle>
                {report.valid ? "Alle Checks bestanden! ✅" : "Probleme gefunden ⚠️"}
              </AlertTitle>
              <AlertDescription>
                Letzte Prüfung: {new Date(report.timestamp).toLocaleString('de-DE')}
                {!report.valid && ` - ${report.issues.length} Problem(e) gefunden`}
              </AlertDescription>
            </Alert>

            {/* Detaillierte Checks */}
            <Card>
              <CardHeader>
                <CardTitle>Validierungs-Checks</CardTitle>
                <CardDescription>
                  Detaillierte Überprüfung aller SEO-relevanten Konfigurationen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(report.checks.robotsTxtAccessible)}
                      <span className="font-medium">robots.txt erreichbar</span>
                    </div>
                    {getStatusBadge(report.checks.robotsTxtAccessible)}
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(report.checks.sitemapUrlPresent)}
                      <span className="font-medium">Sitemap-Referenz vorhanden</span>
                    </div>
                    {getStatusBadge(report.checks.sitemapUrlPresent)}
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(report.checks.sitemapUrlCorrect)}
                      <span className="font-medium">Sitemap-URL korrekt</span>
                    </div>
                    {getStatusBadge(report.checks.sitemapUrlCorrect)}
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(report.checks.sitemapAccessible)}
                      <span className="font-medium">Sitemap erreichbar</span>
                    </div>
                    {getStatusBadge(report.checks.sitemapAccessible)}
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(report.checks.sitemapValidXml)}
                      <span className="font-medium">Valides XML-Format</span>
                    </div>
                    {getStatusBadge(report.checks.sitemapValidXml)}
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(report.checks.sitemapUrlCount > 0)}
                      <span className="font-medium">URLs in Sitemap: {report.checks.sitemapUrlCount}</span>
                    </div>
                    <Badge variant={report.checks.sitemapUrlCount > 0 ? "default" : "destructive"}>
                      {report.checks.sitemapUrlCount} URLs
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      {getCheckIcon(report.checks.disallowRulesCorrect)}
                      <span className="font-medium">Disallow-Regeln korrekt</span>
                    </div>
                    {getStatusBadge(report.checks.disallowRulesCorrect)}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Probleme */}
            {report.issues.length > 0 && (
              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="text-destructive">
                    Gefundene Probleme ({report.issues.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {report.issues.map((issue, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{issue}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Empfehlungen */}
            {report.recommendations.length > 0 && (
              <Card className="border-yellow-500/50">
                <CardHeader>
                  <CardTitle className="text-yellow-600">
                    Empfehlungen ({report.recommendations.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {report.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Quick Links */}
            <Card>
              <CardHeader>
                <CardTitle>Nützliche Links</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" asChild>
                    <a href="https://praxis-versichert.de/robots.txt" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      robots.txt ansehen
                    </a>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href="https://praxis-versichert.de/sitemap.xml" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Sitemap ansehen
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {!report && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <AlertCircle className="w-16 h-16 text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-muted-foreground mb-2">
                Noch keine Validierung durchgeführt
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                Klicken Sie auf "Jetzt validieren" um die SEO-Konfiguration zu überprüfen
              </p>
              <Button onClick={runValidation} size="lg">
                <RefreshCw className="mr-2 h-4 w-4" />
                Erste Validierung starten
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Automatic Validation Info */}
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertTitle>Automatische Validierung</AlertTitle>
          <AlertDescription>
            Die SEO-Validierung läuft automatisch täglich um 6:00 Uhr.
            Bei Problemen erhalten Sie eine E-Mail-Benachrichtigung.
            {logs.length > 0 && (
              <>
                <br />
                Letzte Ausführung: {format(new Date(logs[0].timestamp), "dd.MM.yyyy 'um' HH:mm 'Uhr'", { locale: de })}
              </>
            )}
          </AlertDescription>
        </Alert>

        {/* Validation History */}
        <Card>
          <CardHeader>
            <CardTitle>Validierungs-Historie</CardTitle>
            <CardDescription>
              Die letzten 30 Validierungen
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : logs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Noch keine Validierungen vorhanden
              </p>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Zeitpunkt</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>URLs</TableHead>
                      <TableHead>Probleme</TableHead>
                      <TableHead>E-Mail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">
                          {format(new Date(log.timestamp), "dd.MM.yyyy HH:mm", { locale: de })}
                        </TableCell>
                        <TableCell>
                          <Badge variant={log.valid ? "default" : "destructive"}>
                            {log.valid ? (
                              <span className="flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                OK
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <XCircle className="h-3 w-3" />
                                Fehler
                              </span>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {log.checks.sitemapUrlCount || "-"}
                        </TableCell>
                        <TableCell>
                          {log.issues.length > 0 ? (
                            <Badge variant="outline">{log.issues.length}</Badge>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          {log.email_sent ? (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              <span className="text-xs">
                                {log.email_sent_at && format(new Date(log.email_sent_at), "HH:mm", { locale: de })}
                              </span>
                            </div>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default SeoValidierung;
